import { useState, useEffect, useCallback } from "react";
import StepCard from "./StepCard";
import QuestionForm from "./QuestionForm";
import FeedbackModal from "../Tax Filling/FeedbackModal";
import ReferralRewardModal from "../../components/ReferralRewardModal";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/SocketContext";

const USACompanyFormation = () => {
  const { currentThemeColors } = useTheme();

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const [steps, setSteps] = useState([
    { step: 1, title: "Company formation checklist", status: "in-progress", progress: 0, answers: {}, comments: {}, file: null },
    { step: 2, title: "Post Formation documents", status: "pending", progress: 0 },
    { step: 3, title: "Tax ID (EIN)", status: "pending", progress: 0 },
  ]);

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [assignedCompanyForm, setAssignedCompanyForm] = useState("LLC");
  const [isInitialized, setIsInitialized] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);

  // Calculate total questions based on company type
  const getTotalQuestions = () => {
    if (assignedCompanyForm === "C-Corp") {
      // C-Corp: Basic Info (6) + President (3) + Secretary (3) + Treasurer (6) + Responsible Party (12) = 30
      return 30;
    } else {
      // LLC: Basic Info (6) + Partner 1 (3) + Partner 2 (3) + Partner 3 (3) + Responsible Party (11) = 26
      return 26;
    }
  };

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch user data");

        const data = await response.json();
        setUserData(data.user);

        // Initialize company form type from user data or default to LLC
        const companyForm = data.user.assignedCompanyForm || "LLC";
        setAssignedCompanyForm(companyForm);

        // Initialize steps
        if (data.user.companySteps && data.user.companySteps.length > 0) {
          setSteps(data.user.companySteps);

          // If we have step 1 data, make sure the company type matches
          const step1 = data.user.companySteps.find(s => s.step === 1);
          if (step1 && step1.answers && step1.answers.businessType) {
            setAssignedCompanyForm(step1.answers.businessType);
          }
        }

        setIsInitialized(true);
      } catch (error) {
        console.error(error);
        setError('Failed to load user data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  useEffect(() => {
    if (userData && isInitialized) {
      const step3Done = steps.some(s => s.step === 3 && s.status === 'submitted');
      if (step3Done && (!userData.usaFeedback || !userData.usaFeedback.rating)) {
        setShowFeedbackModal(true);
      }
    }
  }, [steps, userData, isInitialized]);

  const handleFeedbackSubmit = async (rating, comment) => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Please log in to submit feedback.");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/submit-feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: userData._id,
          rating,
          comment: comment || '',
          serviceType: 'usa'
        }),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseData.message || `Failed to submit feedback: ${response.status}`);
      }

      setUserData(prev => ({
        ...prev,
        usaFeedback: {
          rating,
          comment: comment || '',
          submittedAt: new Date()
        }
      }));
      setShowFeedbackModal(false);
      setShowReferralModal(true);
      // alert("Thank you for your feedback!"); // Removing alert to keep it smooth to the next modal
    } catch (error) {
      console.error('Feedback submission error:', error);
      alert(`Failed to submit feedback: ${error.message || 'Please try again.'}`);
    }
  };

  const handleStepClick = (stepNumber) => {
    if (stepNumber === 1) {
      if (steps[0].status === "submitted") {
        alert("Step 1 has already been submitted and cannot be reopened.");
        return;
      }
      setIsFormVisible(true);
    } else if (stepNumber === 2) {
      navigate("/documents", { state: { openFolder: "Post Formation documents" } });
    } else if (stepNumber === 3) {
      navigate("/documents", { state: { openFolder: "Tax ID (EIN)" } });
    }
  };

  const handleFormSubmit = async (data, isFinalSubmit) => {
    const token = localStorage.getItem("token");

    // Check if business type has changed
    const newBusinessType = data.answers && data.answers.businessType;
    if (newBusinessType && newBusinessType !== assignedCompanyForm) {
      setAssignedCompanyForm(newBusinessType);
    }

    // Determine the new status based on whether this is a final submit
    const newStatus = isFinalSubmit ? "submitted" : "in-progress";

    // Use the progress from data if available
    const progress = (data.progress !== undefined) ? data.progress : steps[0].progress;

    // First, update the UI optimistically
    const updatedSteps = steps.map((s) => {
      if (s.step === 1) {
        return {
          ...s,
          status: newStatus,
          progress: progress,
          answers: data.answers,
          comments: data.comments,
          file: data.file || s.file,
          error: false,
          errorMessage: ''
        };
      }
      // If this is a final submit, update step 2 to in-progress
      if (s.step === 2 && isFinalSubmit) {
        return {
          ...s,
          status: "in-progress"
        };
      }
      return s;
    });

    // Update the UI immediately
    setSteps(updatedSteps);
    setError(null);

    try {
      // Prepare the data in the format expected by the backend
      const requestData = {
        steps: updatedSteps.map(step => ({
          step: step.step,
          title: step.title,
          status: step.status,
          progress: step.progress || 0,
          answers: step.answers || {},
          comments: step.comments || {},
          file: step.file || null
        })),
        answers: data.answers || {},
        comments: data.comments || {},
        file: data.file || null
      };

      console.log('Sending update request:', requestData);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/usa-company-formation/update-steps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      const responseData = await response.json().catch(() => ({}));
      console.log('Update response:', { status: response.status, responseData });

      if (!response.ok) {
        throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
      }

      // If we get here, the update was successful
      if (isFinalSubmit) {
        alert("Form submitted successfully!");
        setIsFormVisible(false);
      }
    } catch (error) {
      console.error('Form submission error:', error);

      // Update steps with error state
      const errorSteps = steps.map((s) => {
        if (s.step === 1) {
          return {
            ...s,
            status: 'in-progress',
            error: true,
            errorMessage: error.message || 'Failed to save progress. Please try again.'
          };
        }
        return s;
      });

      setSteps(errorSteps);
      setError(error.message || 'Failed to save progress. Please try again.');
    }
  };

  // Don't render anything until we've finished initializing
  if (loading || !isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading your USA Company Formation information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 mb-4">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Instruction Banner */}
      <div
        className="relative overflow-hidden rounded-2xl px-5 py-4 shadow-lg opacity-0 animate-fade-in-up border border-white/10"
        style={{
          animationDelay: '0ms',
          animationFillMode: 'forwards',
          background: `linear-gradient(135deg, var(--primary-600), var(--primary-700))`
        }}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMSIgaGVpZ2h0PSIxIiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMDUiLz48L3N2Zz4=')] bg-repeat opacity-50" />
        <div className="relative flex items-center gap-4 text-white">
          <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/20">
            <span className="text-xl">🏢</span>
          </div>
          <div className="flex-1 overflow-hidden">
            <div className="animate-marquee whitespace-nowrap flex gap-12 items-center w-max">
              <span className="text-sm font-semibold tracking-wide flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse" />
                USA Formation: Start with Company Formation Checklist
                <span className="text-white/40">/</span>
                Post Formation Documents
                <span className="text-white/40">/</span>
                Tax ID (EIN)
              </span>
              <span className="text-sm font-medium text-white/80 italic">💬 Need help? Use the chat feature to communicate with our experts!</span>

              {/* Duplicate for seamless loop */}
              <span className="text-sm font-semibold tracking-wide flex items-center gap-2 ml-12">
                <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse" />
                USA Formation: Start with Company Formation Checklist
                <span className="text-white/40">/</span>
                Post Formation Documents
                <span className="text-white/40">/</span>
                Tax ID (EIN)
              </span>
              <span className="text-sm font-medium text-white/80 italic">💬 Need help? Use the chat feature to communicate with our experts!</span>
            </div>
          </div>
        </div>
      </div>

      {/* Page Header */}
      <div className="card p-5 sm:p-7 opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-dark-800 dark:text-white">{userData ? userData.name : ''}</h1>
            {userData?.email && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{userData.email}</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 text-sm text-gray-600 dark:text-gray-400">
            <span className="px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-semibold">
              USA Company Formation
            </span>
            <span className="px-3 py-1 rounded-full bg-secondary-50 dark:bg-secondary-900/30 text-secondary-700 dark:text-secondary-400 font-semibold">
              {assignedCompanyForm}
            </span>
          </div>
        </div>
      </div>

      {/* Steps Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 auto-rows-fr">
        {steps.map((s, index) => (
          <div
            key={`wrapper-${s.step}`}
            className="opacity-0 animate-fade-in-up h-full"
            style={{ animationDelay: `${200 + index * 100}ms`, animationFillMode: 'forwards' }}
          >
            <StepCard
              step={s.step}
              title={s.title}
              status={s.status}
              progress={s.progress}
              totalQuestions={s.step === 1 ? getTotalQuestions() : 1}
              error={s.error || false}
              errorMessage={s.errorMessage || ''}
              submittedAt={s.submittedAt}
              onClick={() => handleStepClick(s.step)}
            />
          </div>
        ))}
      </div>

      {isFormVisible && (
        <QuestionForm
          onClose={() => setIsFormVisible(false)}
          onSubmit={(data, isFinal) => handleFormSubmit(data, isFinal)}
          initialAnswers={steps[0].answers}
          initialComments={steps[0].comments}
          isSubmitted={steps[0].status === "submitted"}
          companyType={assignedCompanyForm}
        />
      )}

      {showFeedbackModal && (
        <FeedbackModal
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleFeedbackSubmit}
        />
      )}

      {showReferralModal && (
        <ReferralRewardModal
          isOpen={showReferralModal}
          onClose={() => setShowReferralModal(false)}
        />
      )}
    </div>
  );
};

export default USACompanyFormation;